import React, { Component } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { ReactComponent as CopyIcon } from '../../icons/copy.svg';
import './SectionMonthly.css';

// Helper function to check if an array of values is entirely zeros
const isMetricZero = (valArray) => {
  return valArray.every(val => parseFloat(val) === 0);
};

class SectionMonthly extends Component {
  constructor(props) {
    super(props);

    const { dataMonthly, metaAnnual } = this.props;
    
    // Safety check for missing or malformed data
    const hasError = !dataMonthly || !Array.isArray(dataMonthly) || dataMonthly.length === 0;

    let initialCategory = '';
    let initialMetric = '';

    if (!hasError) {
      // Find categories that have at least one non-zero metric
      const validCategories = metaAnnual?.filter(catObj => 
        dataMonthly.some(m => m.cat === catObj.cat && !isMetricZero(m.val))
      ) || [];

      if (validCategories.length > 0) {
        initialCategory = validCategories[0].cat;
        
        // Find the first non-zero metric in the selected category
        const metricsInCat = dataMonthly.filter(m => m.cat === initialCategory);
        const firstValidMetric = metricsInCat.find(m => !isMetricZero(m.val));
        
        initialMetric = firstValidMetric ? firstValidMetric.name : metricsInCat[0].name;
      } else {
        // Fallback if the entire dataset is purely zeros
        initialCategory = metaAnnual?.[0]?.cat || dataMonthly[0].cat;
        initialMetric = dataMonthly[0].name;
      }
    }

    this.state = {
      hasError,
      selectedCategory: initialCategory,
      selectedMetric: initialMetric,
      viewType: 'chart', // 'chart' or 'table'
      copyMessage: ''
    };
  }

  // --- Formatting Helper ---
  formatValue = (val, category) => {
    const num = parseFloat(val);
    if (isNaN(num)) return val;

    // Remove decimals if >= 100, otherwise show 2 decimal places
    const decimals = Math.abs(num) >= 100 ? 0 : 2;
    
    // Format with commas and correct decimal precision
    const formattedNum = num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });

    // Prepend $ for financial metrics
    return category === 'fin' ? `$${formattedNum}` : formattedNum;
  };

  handleCategoryChange = (e) => {
    const newCat = e.target.value;
    const { dataMonthly } = this.props;
    
    // Find metrics for the new category and automatically select the first non-zero metric
    const metricsInCat = dataMonthly.filter(d => d.cat === newCat);
    const firstValidMetric = metricsInCat.find(d => !isMetricZero(d.val));
    
    this.setState({ 
      selectedCategory: newCat, 
      selectedMetric: firstValidMetric ? firstValidMetric.name : metricsInCat[0]?.name 
    });
  };

  handleMetricChange = (e) => {
    this.setState({ selectedMetric: e.target.value });
  };

  toggleView = () => {
    this.setState(prevState => ({
      viewType: prevState.viewType === 'chart' ? 'table' : 'chart'
    }));
  };

  copyToClipboard = (metricsToCopy) => {
    const { meta } = this.props;
    const defaultMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Build the Header row for the clipboard
    let header = 'Month';
    metricsToCopy.forEach(metric => {
      const unitLabel = metric.cat === 'fin' ? '' : ` (${metric.unit})`;
      header += `\t${metric.label}${unitLabel}`;
    });
    header += '\n';
    
    // Build the data rows for the clipboard
    let rows = '';
    for (let i = 0; i < 12; i++) {
      const monthLabel = meta?.monthList?.[i]?.label || defaultMonths[i];
      let rowString = monthLabel;
      
      metricsToCopy.forEach(metric => {
        const displayVal = this.formatValue(metric.val[i], metric.cat);
        rowString += `\t${displayVal}`;
      });
      
      rows += rowString + '\n';
    }
    
    const clipboardText = header + rows;

    navigator.clipboard.writeText(clipboardText).then(() => {
      this.setState({ copyMessage: 'Data copied' });
      setTimeout(() => this.setState({ copyMessage: '' }), 3000);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      this.setState({ copyMessage: 'Failed to copy.' });
    });
  };

  render() {
    const { dataMonthly, meta, metaAnnual } = this.props;
    const { hasError, selectedCategory, selectedMetric, viewType, copyMessage } = this.state;

    if (hasError) {
      return (
        <div>
          <div className="results-subsection">
            <div className="error-banner">
              <strong>Warning:</strong> Monthly metrics could not be processed. Data is missing or unavailable.
            </div>
          </div>
        </div>
      );
    }

    // Filter categories to only include those that have at least one non-zero metric
    const availableCategories = metaAnnual?.filter(catObj => 
      dataMonthly.some(m => m.cat === catObj.cat && !isMetricZero(m.val))
    ) || [];

    // All metrics for the selected category
    const availableMetrics = dataMonthly.filter(d => d.cat === selectedCategory);
    
    // Active metric for the Chart view
    const activeMetricObj = availableMetrics.find(d => d.name === selectedMetric) || availableMetrics[0];

    // All non-zero metrics for the Table view
    const activeCategoryMetrics = availableMetrics.filter(m => !isMetricZero(m.val));

    const defaultMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Raw numeric data formatted for Recharts (Chart View Only)
    const chartData = activeMetricObj.val.map((valString, index) => {
      const monthLabel = meta?.monthList?.[index]?.label || defaultMonths[index];
      return {
        month: monthLabel,
        value: parseFloat(valString) || 0 
      };
    });

    // Custom Tooltip Formatter (Chart View Only)
    const formatTooltip = (value) => {
      const formatted = this.formatValue(value, activeMetricObj.cat);
      const suffix = activeMetricObj.cat === 'fin' ? '' : ` ${activeMetricObj.unit}`;
      return [`${formatted}${suffix}`, activeMetricObj.label];
    };

    // Determine what should be copied based on the current view
    const metricsToCopy = viewType === 'table' ? activeCategoryMetrics : [activeMetricObj];

    return (
      <div>
        <div className="results-subsection">
          
          {/* Controls Bar */}
          <div className="monthly-controls">
            <select 
              className="monthly-select" 
              value={selectedCategory} 
              onChange={this.handleCategoryChange}
            >
              {availableCategories.length > 0 ? (
                availableCategories.map(catObj => (
                  <option key={catObj.cat} value={catObj.cat}>
                    {catObj.label}
                  </option>
                ))
              ) : (
                <option value="" disabled>No data available</option>
              )}
            </select>

            <select 
              className="monthly-select metric-select" 
              value={selectedMetric} 
              onChange={this.handleMetricChange}
              disabled={viewType === 'table'} // Disable metric dropdown in table view
              style={{ opacity: viewType === 'table' ? 0.5 : 1, cursor: viewType === 'table' ? 'not-allowed' : 'pointer' }}
            >
              {availableMetrics.map(metric => {
                const isZero = isMetricZero(metric.val);
                return (
                  <option key={metric.name} value={metric.name} disabled={isZero}>
                    {metric.label} {metric.cat !== 'fin' && `(${metric.unit})`}
                  </option>
                );
              })}
            </select>

            {/* Right-Aligned Button Group */}
            <div className="monthly-controls-right">
              <span className="copy-message">{copyMessage}</span>
              <button 
                className="monthly-copy-btn" 
                onClick={() => this.copyToClipboard(metricsToCopy)}
                title={viewType === 'table' ? "Copy Table Data to Clipboard" : "Copy Metric Data to Clipboard"}
                aria-label="Copy to Clipboard"
              >
                <CopyIcon className="copy-icon-svg" />
              </button>
              
              <button className="toggle-btn" onClick={this.toggleView}>
                View as {viewType === 'chart' ? 'Table' : 'Chart'}
              </button>
            </div>
          </div>

          {/* Chart View */}
          {viewType === 'chart' && (
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  
                  {/* Add tick={{ fill: 'YOUR_COLOR' }} to change label colors */}
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: 'var(--text3)', fontSize: 14 }} 
                  />
                  <YAxis 
                    tickFormatter={(val) => this.formatValue(val, activeMetricObj.cat)} 
                    tick={{ fill: 'var(--text3)', fontSize: 14 }} 
                  />
                  
                  <Tooltip formatter={formatTooltip} />
                  <Bar 
                    dataKey="value" 
                    fill={`var(--color-${selectedCategory}, #4f46e5)`} 
                    name={activeMetricObj.label} 
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Table View */}
          {viewType === 'table' && (
            <div style={{ overflowX: 'auto' }}>
              <table className="monthly-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    {activeCategoryMetrics.map(metric => (
                      <th key={metric.name}>
                        {metric.label}
                        {metric.cat !== 'fin' && ` (${metric.unit})`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 12 }).map((_, monthIndex) => {
                    const monthLabel = meta?.monthList?.[monthIndex]?.label || defaultMonths[monthIndex];
                    return (
                      <tr key={monthIndex}>
                        <td>{monthLabel}</td>
                        {activeCategoryMetrics.map(metric => (
                          <td key={metric.name}>
                            {this.formatValue(metric.val[monthIndex], metric.cat)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default SectionMonthly;